<template>
	<div class="ai-chat">
		<div v-if="selectedLabel" class="ai-chat__context px-3 py-2 d-flex align-center">
			<v-icon small color="primary" class="mr-1">mdi-cursor-default-click</v-icon>
			<span class="body-2">Blocco selezionato: <strong>{{ selectedLabel }}</strong></span>
			<v-spacer></v-spacer>
			<v-btn x-small icon title="Togli la selezione" @click="$emit('deselect')"><v-icon small>mdi-close</v-icon></v-btn>
		</div>

		<div ref="chatContainer" class="ai-chat__messages px-3 py-2">
			<div v-if="!messages.length" class="text-center pa-4">
				<v-icon large color="grey lighten-1" class="mb-2">mdi-robot-outline</v-icon>
				<p class="body-2 grey--text text--darken-1 mb-1">Descrivi la mail che vuoi, oppure seleziona un blocco e chiedi una modifica.</p>
				<p class="body-2 grey--text text--darken-1 mb-3">Allega lo screenshot di una mail o di un mockup e chiedi di riprodurlo.</p>
				<v-chip v-for="(a, i) in quickActions" :key="'qa' + i" small outlined class="ai-chat__chip mr-1 mb-1" @click="sendPrompt(a)">{{ a }}</v-chip>
			</div>

			<div v-for="(msg, i) in messages" :key="i" :class="['ai-chat__msg', 'ai-chat__msg--' + msg.role]">
				<div class="ai-chat__bubble">
					<div v-if="msg.content">{{ msg.content }}</div>
					<img v-if="msg.image" :src="imageSrc(msg.image)" class="ai-chat__bubble-img" alt="">
				</div>
				<div v-if="msg.review" class="caption mt-1 grey--text">
					<v-icon x-small class="mr-1">mdi-eye-check-outline</v-icon>Seconda passata: ha riguardato il render
				</div>
				<div v-if="msg.role === 'assistant' && (msg.applied || (msg.rejected && msg.rejected.length))" class="caption mt-1">
					<span v-if="msg.applied" class="grey--text text--darken-1">{{ msg.applied }} {{ msg.applied === 1 ? 'modifica applicata' : 'modifiche applicate' }}</span>
					<span v-if="msg.rejected && msg.rejected.length" class="orange--text text--darken-3">
						{{ msg.applied ? ' · ' : '' }}{{ msg.rejected.length }} {{ msg.rejected.length === 1 ? 'scartata' : 'scartate' }}: {{ rejectedReasons(msg) }}
					</span>
				</div>
				<div v-if="msg.role === 'assistant' && msg.imagesTotal" class="caption mt-1">
					<v-progress-circular v-if="msg.imagesDone + msg.imagesFailed < msg.imagesTotal" indeterminate size="12" width="2" color="primary" class="mr-1"></v-progress-circular>
					<span class="grey--text text--darken-1">{{ imagesLabel(msg) }}</span>
				</div>
			</div>

			<div v-if="busy" class="ai-chat__thinking">
				<v-icon color="primary" size="34" class="ai-chat__robot">{{ robotIcon }}</v-icon>
				<p class="caption mb-0 mt-1 grey--text text--darken-1">
					{{ phaseLabel }}<span class="ai-chat__dots"><i>.</i><i>.</i><i>.</i></span>
				</p>
			</div>
		</div>

		<div v-if="suggestions.length && !busy" class="px-3 pb-2">
			<v-chip v-for="(s, i) in suggestions" :key="'s' + i" small outlined class="ai-chat__chip mr-1 mb-1" @click="sendPrompt(s)">{{ s }}</v-chip>
		</div>

		<v-alert v-if="error" type="error" dense dismissible class="mx-3 mb-2 body-2" @input="$emit('clear-error')">{{ error }}</v-alert>

		<v-divider></v-divider>

		<div class="pa-3">
			<div v-if="imageLoading || pendingImage || imageError" class="ai-chat__attachment mb-2">
				<v-chip v-if="imageLoading" small>
					<v-progress-circular indeterminate size="14" width="2" class="mr-2"></v-progress-circular>
					Preparo l'immagine…
				</v-chip>
				<v-chip v-else-if="pendingImage" close small @click:close="removeImage">
					<v-avatar left size="28"><img :src="pendingImage.dataUrl" alt=""></v-avatar>
					Immagine allegata
				</v-chip>
				<span v-if="imageError" class="caption error--text">{{ imageError }}</span>
			</div>
			<div class="d-flex align-end">
				<v-btn icon :disabled="busy || imageLoading" title="Allega screenshot" @click="$refs.imageInput.click()">
					<v-icon>mdi-paperclip</v-icon>
				</v-btn>
				<input ref="imageInput" type="file" accept="image/*" style="display:none" @change="onFileChosen">
				<v-textarea
					v-model="inputText"
					placeholder="Scrivi cosa vuoi fare…"
					outlined dense hide-details rows="1" row-height="20" auto-grow
					class="mr-2"
					@keydown.enter.exact.prevent="sendPrompt(inputText)">
				</v-textarea>
				<v-btn icon color="primary" :disabled="busy || imageLoading || (!inputText.trim() && !pendingImage)" title="Invia (Invio)" @click="sendPrompt(inputText)">
					<v-icon>mdi-send</v-icon>
				</v-btn>
			</div>
			<div class="d-flex mt-2">
				<v-tooltip top>
					<template v-slot:activator="{ on }">
						<v-btn x-small text color="info" v-on="on" :disabled="!canUndo || busy" @click="$emit('undo')">
							<v-icon x-small left>mdi-undo</v-icon>Annulla ultima modifica
						</v-btn>
					</template>
					<span>Annulla l'ultima modifica al template, anche se fatta dall'assistente</span>
				</v-tooltip>
				<v-spacer></v-spacer>
				<v-btn x-small text :disabled="!messages.length || busy" @click="$emit('clear-chat')">
					<v-icon x-small left>mdi-delete-outline</v-icon>Svuota chat
				</v-btn>
			</div>
		</div>
	</div>
</template>

<script>
/**
 * Chat dell'assistente AI del builder. Solo presentazione: lo stato (messaggi, richiesta
 * al backend, applicazione delle operazioni) vive in AIBuilder.
 */
export default {
	name: 'ai-chat',

	props: {
		messages: { type: Array, default: function() { return [] } },
		suggestions: { type: Array, default: function() { return [] } },
		busy: { type: Boolean, default: false },
		error: { type: String, default: null },
		selectedLabel: { type: String, default: '' },
		canUndo: { type: Boolean, default: false },
		// 'compose' mentre scrive la mail, 'review' mentre si rilegge il render
		phase: { type: String, default: null }
	},

	data() {
		return {
			inputText: '',
			robotFrame: 0,
			robotTimer: null,
			pendingImage: null,
			imageLoading: false,
			imageError: null,
			lastSentImage: null,
			quickActions: [
				'Crea una newsletter con le novità del mese',
				'Prepara una promozione con griglia prodotti',
				'Accorcia tutti i testi',
				'Aggiungi il footer con i dati aziendali'
			]
		}
	},

	computed: {
		// Tre fotogrammi veri, non una rotella: si vede che sta pensando
		robotIcon() {
			return ['mdi-robot-outline', 'mdi-robot-confused-outline', 'mdi-robot-excited-outline'][this.robotFrame % 3]
		},

		// L'attesa lunga e' quella in cui compone: dire QUALE passo sta facendo la accorcia
		phaseLabel() {
			if (this.phase === 'review') return 'Mi rileggo la mail come viene davvero'
			return 'Sto componendo la mail'
		}
	},

	watch: {
		messages: {
			handler() {
				var self = this
				this.$nextTick(function() { self.scrollToBottom() })
			},
			deep: true
		},
		busy(attivo) {
			var self = this
			this.$nextTick(function() { self.scrollToBottom() })

			clearInterval(this.robotTimer)
			this.robotTimer = null
			if (!attivo) return
			this.robotFrame = 0
			this.robotTimer = setInterval(function() { self.robotFrame++ }, 600)
		}
	},

	beforeDestroy() {
		clearInterval(this.robotTimer)
	},

	methods: {
		sendPrompt(text) {
			if (this.busy || this.imageLoading) return
			var trimmed = (text || '').trim()
			if (!trimmed && !this.pendingImage) return
			if (!trimmed) trimmed = 'Riproduci questa mail con i blocchi disponibili'

			var image = this.pendingImage ? { media_type: this.pendingImage.media_type, data: this.pendingImage.data } : null
			this.lastSentImage = this.pendingImage
			this.$emit('send', trimmed, image)
			this.inputText = ''
			this.pendingImage = null
			this.imageError = null
		},

		// Il padre la chiama quando la richiesta fallisce: l'immagine non è mai partita,
		// rimetterla nel chip evita di riselezionare e ridurre di nuovo il file
		restoreImage() {
			if (this.lastSentImage && !this.pendingImage) this.pendingImage = this.lastSentImage
			this.lastSentImage = null
		},

		// Le foto arrivano dopo la risposta: la riga si aggiorna mentre si riempiono i blocchi
		imagesLabel(msg) {
			var pending = msg.imagesTotal - msg.imagesDone - msg.imagesFailed
			if (pending > 0) return 'Genero le immagini… ' + (msg.imagesDone + msg.imagesFailed) + ' di ' + msg.imagesTotal
			var done = msg.imagesDone === 1 ? '1 immagine generata' : msg.imagesDone + ' immagini generate'
			if (!msg.imagesFailed) return done
			return (msg.imagesDone ? done + ' · ' : '') + msg.imagesFailed + ' non riuscite: il segnaposto resta col suo prompt'
		},

		rejectedReasons(msg) {
			return msg.rejected.map(function(r) { return r.reason }).join('; ')
		},

		scrollToBottom() {
			var c = this.$refs.chatContainer
			if (c) c.scrollTop = c.scrollHeight
		},

		onFileChosen(e) {
			var file = e.target.files && e.target.files[0]
			e.target.value = ''
			if (!file) return
			this.imageError = null
			this.pendingImage = null
			this.imageLoading = true
			this.reduceImage(file)
		},

		// Riduce lato lungo a 1568px (mai upscale), JPEG 0.85 senza alfa: uno screenshot
		// pesa 2-10 MB a 3000-4000px, oltre 1568px il modello ridimensiona da solo pagando token
		reduceImage(file) {
			var self = this
			var fail = function() {
				self.imageLoading = false
				self.pendingImage = null
				self.imageError = 'Non riesco a leggere questa immagine: prova con un JPEG o un PNG.'
			}
			var reader = new FileReader()
			reader.onerror = fail
			reader.onload = function(ev) {
				var img = new Image()
				img.onerror = fail
				img.onload = function() {
					try {
						var maxSide = 1568
						var scale = Math.min(1, maxSide / Math.max(img.naturalWidth, img.naturalHeight))
						var w = Math.round(img.naturalWidth * scale)
						var h = Math.round(img.naturalHeight * scale)
						var canvas = document.createElement('canvas')
						canvas.width = w
						canvas.height = h
						var ctx = canvas.getContext('2d')
						ctx.fillStyle = '#fff'
						ctx.fillRect(0, 0, w, h)
						ctx.drawImage(img, 0, 0, w, h)
						var dataUrl = canvas.toDataURL('image/jpeg', 0.85)
						self.pendingImage = {
							dataUrl: dataUrl,
							media_type: 'image/jpeg',
							data: dataUrl.split(',')[1]
						}
						self.imageLoading = false
					} catch (err) {
						fail()
					}
				}
				img.src = ev.target.result
			}
			reader.readAsDataURL(file)
		},

		removeImage() {
			this.pendingImage = null
			this.imageError = null
		},

		imageSrc(image) {
			return 'data:' + image.media_type + ';base64,' + image.data
		}
	}
}
</script>

<style scoped>
/* Vuetify di default usa 28px: troppo arioso rispetto al resto del builder */
.v-textarea >>> textarea {
	line-height: 20px;
}

.ai-chat {
	display: flex;
	flex-direction: column;
	height: 100%;
	background: #fff;
}

/* Proposte cliccabili: grigio scuro fisso, mai il colore del tenant (giallo o arancio non si leggono) */
.ai-chat__chip.v-chip {
	color: rgba(0, 0, 0, 0.78) !important;
	border-color: rgba(0, 0, 0, 0.3) !important;
	background: #fff !important;
}

.ai-chat__chip.v-chip:hover {
	border-color: rgba(0, 0, 0, 0.6) !important;
	background: #f5f5f5 !important;
}

.ai-chat__context {
	background: #e3f2fd;
	border-bottom: 1px solid #bbdefb;
}

.ai-chat__messages {
	flex: 1;
	min-height: 0;
	overflow-y: auto;
}

.ai-chat__msg {
	display: flex;
	flex-direction: column;
	margin-bottom: 10px;
}

.ai-chat__msg--user {
	align-items: flex-end;
}

.ai-chat__bubble {
	max-width: 92%;
	padding: 8px 12px;
	border-radius: 10px;
	font-size: 14px;
	line-height: 1.45;
	white-space: pre-wrap;
	background: #f1f3f4;
	color: #212121;
}

.ai-chat__msg--user .ai-chat__bubble {
	background: var(--v-primary-base, #1976d2);
	color: #fff;
}

.ai-chat__bubble-img {
	display: block;
	max-width: 100%;
	max-height: 160px;
	border-radius: 6px;
	margin-top: 6px;
}

.ai-chat__attachment img {
	width: 100%;
	height: 100%;
	object-fit: cover;
}
.ai-chat__thinking {
	text-align: center;
	padding: 14px 8px 18px;
}

/* Il robot cambia faccia ogni 600ms e respira: l'attesa e' lunga (40s e piu'),
   una rotella ferma la fa sembrare bloccata */
.ai-chat__robot {
	animation: ai-chat-breathe 1.2s ease-in-out infinite;
}

@keyframes ai-chat-breathe {
	0%, 100% { transform: translateY(0) scale(1); }
	50% { transform: translateY(-3px) scale(1.06); }
}

.ai-chat__dots i {
	font-style: normal;
	animation: ai-chat-dot 1.2s infinite;
}

.ai-chat__dots i:nth-child(2) { animation-delay: 0.2s; }
.ai-chat__dots i:nth-child(3) { animation-delay: 0.4s; }

@keyframes ai-chat-dot {
	0%, 60%, 100% { opacity: 0.2; }
	30% { opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
	.ai-chat__robot, .ai-chat__dots i { animation: none; }
}
</style>
